import apiClient from "../clients/apiClient";
import { ApiResponse } from "../schemas/apiResponseSchema";

export type NicMultilingual = { en?: string; si?: string; ta?: string };

export type CiCustomerNicRecord = {
    id: number;
    nic_number: string;
    name?: NicMultilingual | null;
    address?: NicMultilingual | null;
    sex?: string | null;
    date_of_issue?: string | null;
    date_of_birth?: string | null;
    place_of_birth?: NicMultilingual | null;
    photo?: { front?: string; back?: string } | null;
    is_verified?: number | null;
};

export type NicLookupResult = {
    found: boolean;
    record?: CiCustomerNicRecord | null;
    message?: string;
    customer?: Record<string, any> | null;
};

export type NicExtractedDetails = {
    name?: NicMultilingual;
    address?: NicMultilingual;
    place_of_birth?: NicMultilingual | null;
    sex?: string | null;
    date_of_issue?: string | null;
    date_of_birth?: string | null;
};

export type NicPhotosResponse = {
    front?: string | null;
    back?: string | null;
    front_url?: string | null;
    back_url?: string | null;
};

export async function getCustomerNicPhotos(customerId: number): Promise<NicPhotosResponse> {
    try {
        const response: ApiResponse<NicPhotosResponse> = (
            await apiClient.get(`/nic/customer/${customerId}/photos`)
        ).data;
        return response.data;
    } catch (error: any) {
        return { front: null, back: null, front_url: null, back_url: null };
    }
}

export async function lookupNic(nic: string): Promise<NicLookupResult> {
    try {
        const response: ApiResponse<NicLookupResult> = (
            await apiClient.get(`/nic/lookup/${encodeURIComponent(nic)}`)
        ).data;
        return response.data;
    } catch (error: any) {
        handleApiError(error, "Failed to look up NIC");
    }
}

export async function createNic(nic: string): Promise<CiCustomerNicRecord> {
    try {
        const response: ApiResponse<CiCustomerNicRecord> = (
            await apiClient.post(`/nic/${encodeURIComponent(nic)}`)
        ).data;
        return response.data;
    } catch (error: any) {
        handleApiError(error, "Failed to register NIC");
    }
}

export async function uploadNicPhotos(nicId: number, front: File, back: File): Promise<CiCustomerNicRecord> {
    try {
        const formData = new FormData();
        formData.append('front', front);
        formData.append('back', back);

        const response: ApiResponse<CiCustomerNicRecord> = (
            await apiClient.post(`/nic/${nicId}/upload-photos`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            })
        ).data;
        return response.data;
    } catch (error: any) {
        handleApiError(error, "Failed to upload NIC photos");
    }
}

export async function extractNic(nicId: number): Promise<NicExtractedDetails> {
    try {
        const response: ApiResponse<NicExtractedDetails> = (
            await apiClient.post(`/nic/${nicId}/extract`)
        ).data;
        return response.data;
    } catch (error: any) {
        handleApiError(error, "Failed to extract NIC details");
    }
}

export async function updateNic(nicId: number, payload: NicExtractedDetails): Promise<CiCustomerNicRecord> {
    try {
        const response: ApiResponse<CiCustomerNicRecord> = (
            await apiClient.put(`/nic/${nicId}`, payload)
        ).data;
        return response.data;
    } catch (error: any) {
        handleApiError(error, "Failed to save NIC details");
    }
}

export function isValidNicFormat(nic: string): boolean {
    const value = nic.trim().toUpperCase();
    if (value.length === 10) {
        return /^[0-9]{9}[VX]$/.test(value);
    }
    if (value.length === 12) {
        return /^[0-9]{12}$/.test(value);
    }
    return false;
}

function handleApiError(error: any, defaultMessage: string): never {
    if (error.response) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
            const messages = detail.map((err: any) => {
                const field = err.loc?.[err.loc.length - 1];
                return `${field}: ${err.msg}`;
            });
            throw new Error(messages.join(", "));
        }
        if (typeof detail === "string") throw new Error(detail);
        throw new Error(defaultMessage);
    }
    throw new Error(defaultMessage);
}
